import { Component, inject, OnInit } from '@angular/core';
import { InputComponent } from '@shared/components/input/input.component';
import { ButtonComponent } from '@shared/components/button/button.component';
import { BUTTON_SIZE, BUTTON_TYPES } from '@shared/components/button/button-types.enum';
import { InfoComponent } from '@shared/components/info/info.component';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { PASSWORD_REQUIREMENTS } from '../auth/signup/signup.const';
import { PasswordValidationComponent } from '@shared/components/password-validation/password-validation.component';
import { FranchiseInputComponent, IFranchiseItem } from '@shared/components/franchise-input/franchise-input.component';
import { IFullFranchiseItem, IHome_FranchisesResponse, ISearchableType } from '@shared/constants/models.const';
import { switchMap, take } from 'rxjs';
import { ContentService } from '../../services/content.service';
import { AuthenticationService } from '../../services/authentication.service';
import { MatDialog } from '@angular/material/dialog';
import { DeleteAccountModalComponent } from './delete-account-modal/delete-account-modal.component';
import { matchValidator, NAME_REGEX, notEmpty, passwordFormat, PASTE_NAME_REGEX } from '../auth/validators.const';
import { TranslateModule } from '@ngx-translate/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { IPasswordValidation } from '../auth/signup/signup.component';
import { HelperService } from '../../services/helper.service';
import { MultiSelectComponent } from '@shared/components/multi-select/multi-select.component';
import {
  CommunicationsDropdownService,
} from '@shared/components/communications-dropdown/communications-dropdown.service';
import { FranchiseService } from '../../services/franchise.service';
import { E_FR_LOC } from '@shared/constants/enums.const';
import { MessageToasterComponent, ToasterType } from '@shared/components/message-toaster/message-toaster.component';
import { MatSnackBar } from '@angular/material/snack-bar';

export interface IProfileUser {
  email: string;
  given_name: string;
  family_name: string;
  ['custom:location']: string;
  ['custom:internal']?: string;
  ['custom:preferred_content']: string[];
}

@UntilDestroy()
@Component({
  selector: 'app-user-profile',
  imports: [
    InputComponent,
    ButtonComponent,
    InfoComponent,
    ReactiveFormsModule,
    PasswordValidationComponent,
    FranchiseInputComponent,
    TranslateModule,
    MultiSelectComponent,
  ],
  templateUrl: './user-profile.component.html',
  styleUrl: './user-profile.component.scss',
})
export class UserProfileComponent implements OnInit {
  readonly dialog = inject(MatDialog);
  frItems: IHome_FranchisesResponse[] | undefined = [];
  userFr: IFullFranchiseItem | undefined;
  userFrRoute: string = '';
  email = new FormControl({ value: '', disabled: true }, Validators.required);
  user!: IProfileUser;
  userForm!: FormGroup;
  passwordForm!: FormGroup;
  preferredContentOptions: string[] = [];
  currentPassRequirements!: IPasswordValidation[];
  preselectedContent: string[] = [];

  isPersonalInfoSubmitted = false;
  isPasswordSubmit = false;
  arePasswordRequirementsMet = false;

  protected readonly ButtonTypes = BUTTON_TYPES;
  protected readonly BUTTON_SIZE = BUTTON_SIZE;
  protected readonly passwordRequirements: IPasswordValidation[] = PASSWORD_REQUIREMENTS;
  protected readonly PASTE_NAME_REGEX = PASTE_NAME_REGEX;
  protected readonly NAME_REGEX = NAME_REGEX;

  snackBar = inject(MatSnackBar);
  private readonly CONTENT = inject(ContentService);

  constructor(
    private authService: AuthenticationService,
    private fb: FormBuilder,
    private HELPER: HelperService,
    private commsDropdownService: CommunicationsDropdownService,
    private franchiseService: FranchiseService,
  ) {
  }

  get isSubmitProfileAvailable(): boolean {
    return !!this.userForm.value['family_name'] &&
      !!this.userForm.value['given_name'] &&
      !!this.userForm.value['custom:location']?.length &&
      !!this.userForm.value['custom:preferred_content']?.length;
  }

  get isSubmitPasswordAvailable(): boolean {
    return !!this.passwordForm.value.currentPassword &&
      !!this.passwordForm.value.newPassword &&
      !!this.passwordForm.value.confirmPassword &&
      this.arePasswordRequirementsMet;
  }

  ngOnInit() {

    // clear previews page information; need for correct routing
    this.HELPER.$pageData.next(null);

    this.currentPassRequirements = [...this.passwordRequirements];
    this.authService.getUser().then(user => {
      if (user) {
        this.user = user.attributes;
        this.userFrRoute = user.attributes['custom:location'] ?? E_FR_LOC.en;
        this.preselectedContent = user.attributes['custom:preferred_content']?.split(',');
        this.createUserForm();
        this.getFrList();
      }
      this.createPasswordForm();

    });
    this.HELPER.rootFolderId = 0;
  }

  createUserForm(): void {
    this.userForm = this.fb.group({
      email: [{ value: this.user.email, disabled: true }],
      family_name: [this.user.family_name, [Validators.required, Validators.pattern(NAME_REGEX), notEmpty]],
      given_name: [this.user.given_name, [Validators.required, Validators.pattern(NAME_REGEX), notEmpty]],
      ['custom:location']: [this.user['custom:location'] ?? E_FR_LOC.en, Validators.required],
      ['custom:preferred_content']: ['', Validators.required],
    });
  }

  createPasswordForm(): void {
    this.passwordForm = this.fb.group({
      currentPassword: ['', Validators.required],
      newPassword: ['', [Validators.required, passwordFormat]],
      confirmPassword: ['', [Validators.required, passwordFormat]],
    }, { validators: matchValidator('newPassword', 'confirmPassword') });

    this.validatePasswordHint();
  }

  validatePasswordHint(): void {
    this.passwordForm.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
      this.currentPassRequirements.map(requirement => {
        const givenName = this.userForm.value.given_name?.toLowerCase();
        const familyName = this.userForm.value.family_name?.toLowerCase();
        const email = this.user.email?.split('@')[0]?.toLowerCase();
        const password = this.passwordForm.value.newPassword?.toLowerCase();
        if (requirement.validation === 'NAME_MATCH' && givenName && familyName && email && password) {
          requirement.valid = !password?.includes(givenName) && !password?.includes(familyName) && !password?.includes(email);
        } else if (this.passwordForm.value.newPassword) {
          let regex = new RegExp(requirement.validation);
          requirement.valid = regex.test(String(this.passwordForm.value.newPassword));
        }
      });
      this.arePasswordRequirementsMet = this.currentPassRequirements.every(req => req.valid);
    });
  }

  getFrList(): void {
    this.franchiseService.getFranchisesList()
      .pipe(untilDestroyed(this))
      .subscribe((list) => {
        this.frItems = list;
        this.getUserFr();
        this.setCurrentContentOptions();
      });
  }

  getUserFr(): void {
    this.frItems?.map(fr => {
      fr.localizations.forEach((localization) => {
        if (localization?.route === this.userFrRoute) {
          this.userFr = { ...localization };
        }
      });
    });
  }

  setCurrentContentOptions(): void {
    const newVal: any[] = [];
    this.userFr?.searchableContentTagTypes?.forEach((tag: ISearchableType) => {
      newVal.push(tag.name);
    });
    this.preferredContentOptions = newVal;
  }

  savePersonalInfo() {
    this.isPersonalInfoSubmitted = true;
    if (this.userForm.valid) {
      this.userForm.controls['family_name'].setValue(this.userForm.value['family_name'].trim());
      this.userForm.controls['given_name'].setValue(this.userForm.value['given_name'].trim());
      this.authService.updateUserData(this.userForm.value);
      this.setCurrentContentOptions();

      this.authService.user
        .pipe(
          take(1),
          switchMap(() => this.CONTENT.saveMySettings(null, this.userFrRoute)),
        )
        .subscribe();
    }
  }

  saveCommunication() {
    this.CONTENT.saveCommunications(this.commsDropdownService.selectedFoldersIds).pipe(take(1)).subscribe(data => {
      if (data) {
        const message = 'The changes have been saved';
        this.openMessage(ToasterType.SUCCESS, message);
      }
    });
  }

  changePassword() {
    this.isPasswordSubmit = true;
    if (this.passwordForm.valid) {
      this.authService.changePassword(
        this.passwordForm.value.currentPassword,
        this.passwordForm.value.newPassword,
      ).then(() => this.passwordForm.reset());
    }
  }

  deleteAccount() {
    this.dialog.open(DeleteAccountModalComponent, { panelClass: 'custom-dialog' })
      .afterClosed()
      .pipe(take(1))
      .subscribe(isConfirmed => {
        if (isConfirmed) {
          this.authService.deleteAccount();
        }
      });
  }

  selectedItems(items: string[]): void {
    this.userForm.controls['custom:preferred_content'].setValue(items);
  }

  changeLocation(frItem: IFranchiseItem) {
    this.userForm.controls['custom:location'].setValue(frItem.route);
    this.changeContentOptions(frItem);
  }

  changeContentOptions(frItem: IFranchiseItem): void {
    if (this.userFrRoute !== frItem.route) {
      this.userFrRoute = frItem.route;
      this.preselectedContent = [];
      this.preferredContentOptions = [];
      this.getUserFr();
      this.setCurrentContentOptions();
    }
  }

  openMessage(type: ToasterType, message: string, title?: string) {
    this.snackBar.openFromComponent(MessageToasterComponent, {
      data: {
        message: message,
        title: title,
      },
      panelClass: type,
      duration: 5000,
    });
  }
}
